import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import {
	BIRTHDAY_FORMAT
} from '../utils/constants';
import {
	BasePatient,
	CustomerInfo,
	CustomerInfoUpdateBody,
	ExpandedPatient,
	PatientRecordPatient,
	PatientRecordVisit,
} from '../utils/dataTypes';
import { pimsApi } from './pimsApi';

// Define a service using a base URL and expected endpoints
export const admissionApi = pimsApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * GET
		 */
		searchCustomerByPhone: builder.query<CustomerInfo[], string>({
			query: (phone) => `customer/search?query=${phone}`,
			providesTags: (result, error, id) => [{ type: 'Customer', id }],
			transformResponse: (response: { data: CustomerInfo[] }) =>
				response.data,
		}),
		getCustomerById: builder.query<CustomerInfo, string>({
			query: (customerId) => `customer/${customerId}`,
			providesTags: (result, error, id) => [{ type: 'Customer', id }],
			transformResponse: (response: { data: CustomerInfo }) =>
				response.data,
		}),
		getPetsByCustomerId: builder.query<ExpandedPatient[], string>({
			query: (customerId) => `customer/${customerId}/relationship`,
			providesTags: (result, error, id) => [{ type: 'Patient', id }],
			transformResponse: (response: { data: ExpandedPatient[] }) =>
				response.data,
		}),
		listCustomer: builder.query<CustomerInfo[], void>({
            query: () => 'customer',
            providesTags: () => [{ type: 'Customer' }],
            transformResponse: (response: { data: CustomerInfo[] }) => {
                return response.data;
            },
        }),
		getCustomerPets: builder.query<BasePatient[], string>({
            query: (customerId) => `customer/${customerId}/pets`,
            providesTags: (_result, _error, customerId) => [{ type: 'CUSTOMER_PETS', customerId }],
            transformResponse: (response: { data: BasePatient[] }) => {
                return response.data;
            },
        }),

		/**
		 * Add/POST new
		 */
		submitNewPatient: builder.mutation<
			PatientRecordVisit,
			{ newPatientData: any }
		>({
			async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
				const { newPatientData } = _arg;
				const formattedBirthday = newPatientData.birthday
					? newPatientData.birthday.format(BIRTHDAY_FORMAT)
					: null;
				const formattedName = newPatientData.name
					? newPatientData.name
					: null;
				const formattedBreed = newPatientData.breed
					? newPatientData.breed
					: null;
				const formattedSpecies = newPatientData.species
					? newPatientData.species
					: null;
				const formattedIsIntact = newPatientData.is_intact !== null && newPatientData.is_intact !== undefined
					? newPatientData.is_intact
					: null;
				const formattedNote = newPatientData.note
					? newPatientData.note
					: null;
				const formattedDeceasedAt = newPatientData.deceased_at
					? newPatientData.deceased_at
					: null;
				const formattedSex = newPatientData.sex
					? newPatientData.sex
					: null;
				const patientBody = {
					birthday: formattedBirthday,
					name: formattedName,
					breed: formattedBreed,
					species: formattedSpecies,
					sex: formattedSex,
					is_intact: formattedIsIntact,
					deceased_at: formattedDeceasedAt,
					note: formattedNote,
					alerts: newPatientData.alerts ?? null,
				}
				// Add new patient to DB
				const postNewPatient = await fetchWithBQ({
					url: `pet`,
					method: 'POST',
					body: patientBody,
				});
				if (postNewPatient.error) throw postNewPatient.error;

				const newPatient = (
					postNewPatient.data as {
						data: PatientRecordPatient;
					}
				).data;

				const postNewVisit = await fetchWithBQ({
					url: `visit`,
					method: 'POST',
					body: {
						pet_id: newPatient.pet_id,
						status: 'arrived', // TODO should this default to `arrived`?
						reason_for_visit: newPatientData.reason_for_visit || null,
					},
				});
				if (postNewVisit.error) throw postNewVisit.error;

				const newVisit = (
					postNewVisit.data as {
						data: PatientRecordVisit;
					}
				).data;

				if (newPatientData.customer_id) {
					const attachCustomerToVisit = await fetchWithBQ({
						url: `visit/${newVisit.id}`,
						method: 'PUT',
						body: {
							owner_id: newPatientData.customer_id,
							rdvm_id:  null,
							discharge_summary_location: null,
							recheck_time: null,
							override_display_name: null,
							override_email: null,
						},
					});
					if (attachCustomerToVisit.error)
						throw attachCustomerToVisit.error;

					const attachCustomerToPet = await fetchWithBQ({
						url: `customer/${newPatientData.customer_id}/relationship`,
						method: 'PUT',
						body: { pet_id: newPatient.pet_id, is_primary: false },
					});

					if (attachCustomerToPet.error)
						throw attachCustomerToPet.error;
				}

				return { data: newVisit };
			},
			invalidatesTags: (result, error, id) => [
				{ type: 'Visit', id: 'LIST' },
			],
		}),
		addNewPetVisitCheckin: builder.mutation<
			null,
			{ name: string, mobile_phone: string }
		>({
			query({ name, mobile_phone }) {
				return {
					url: `pet_visit_checkin`,
					method: 'POST',
					body: {
						name,
						mobile_phone,
					},
				};
			},
			invalidatesTags: (result, error,) => [
				{ type: 'Visit', id: 'LIST' },
			],
		}),
		addNewCustomer: builder.mutation<
			CustomerInfo,
			{ visit_id: number; pet_id: string; body: CustomerInfoUpdateBody }
		>({
			async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
				const { visit_id, pet_id } = _arg;
				const visitQuery  = await fetchWithBQ(`visit/${visit_id}`);
				const currentVisit = visitQuery.data as PatientRecordVisit | undefined; // Unfortunately no way around this cast https://redux-toolkit.js.org/rtk-query/usage/customizing-queries

				// Add new customer to DB
				const postNewCustomer = await fetchWithBQ({
					url: `customer`,
					method: 'POST',
					body: _arg.body,
				});
				
				if (postNewCustomer.error) {
					return { error: postNewCustomer.error.data  as FetchBaseQueryError};
				}

				//Attach new customer to the current visit
				const newCustomer = postNewCustomer.data as {
					data: CustomerInfo;
				};
				const { customer_id: owner_id } = newCustomer.data;

				const attachCustomerToVisit = await fetchWithBQ({
					url: `visit/${visit_id}`,
					method: 'PUT',
					body: {
						owner_id,
						rdvm_id: currentVisit?.rdvm_id || null,
						discharge_summary_location: null,
						recheck_time: currentVisit?.recheck_time || null,
						override_display_name: null,
						override_email: null,
					},
				});
				if (attachCustomerToVisit.error)
					throw attachCustomerToVisit.error;

				//Attach new customer to current patient
				const attachCustomerToPet = await fetchWithBQ({
					url: `customer/${owner_id}/relationship`,
					method: 'PUT',
					body: { pet_id, is_primary: false }, //TODO for now, `is_primary` is always sent as false,
				});

				return attachCustomerToPet.data
					? { data: attachCustomerToPet.data as CustomerInfo }
					: {
							error: attachCustomerToPet.error as FetchBaseQueryError,
					  };
			},

			invalidatesTags: (result, error, { visit_id, pet_id }) => {
				return error ? [] : [
					{ type: 'Visit', id: visit_id },
					{ type: 'Patient', id: pet_id },
					{ type: 'Customer' },
				]
			},
		}),

		/**
		 * MUTATIONS
		 */

		modifyCustomer: builder.mutation<
			CustomerInfo,
			{ customer_id: string; body: CustomerInfoUpdateBody }
		>({
			query({ customer_id, body }) {
				return {
					url: `customer/${customer_id}`,
					method: 'PUT',
					body,
				};
			},
			transformResponse: (response: { data: CustomerInfo }) => {
				return response.data;
			},
			transformErrorResponse: (response) => {
				return response.data;
			},
			invalidatesTags: (result, error, { customer_id }) => {
				return error ? [] : [
					{ type: 'Customer', id: customer_id },
				]
			},
		}),
		// mergePatients: builder.mutation<
		// 	CustomerInfo,
		// 	{ customer_id: string; body: CustomerInfo }
		// >({
		// 	query({ customer_id, body }) {
		// 		return {
		// 			url: `customer/${customer_id}`,
		// 			method: 'PUT',
		// 			body,
		// 		};
		// 	},
		// 	transformResponse: (response: { data: CustomerInfo }) => {
		// 		return response.data;
		// 	},
		// 	invalidatesTags: (result, error, { customer_id }) => [
		// 		{ type: 'Customer', id: customer_id },
		// 	],
		// }),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
	useSearchCustomerByPhoneQuery,
	useGetCustomerByIdQuery,
	useGetPetsByCustomerIdQuery,
	useSubmitNewPatientMutation,
	useAddNewCustomerMutation,
	useAddNewPetVisitCheckinMutation,
	useModifyCustomerMutation,
	useListCustomerQuery,
	useGetCustomerPetsQuery,
} = admissionApi;
